import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/content";

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const related = article
    ? articles.filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug).slice(0, 3)
    : [];
  const relatedFill =
    related.length > 0
      ? related
      : articles.filter((a) => a.slug !== slug).sort((a, b) => b.hot - a.hot).slice(0, 3);

  useSeo({
    title: article ? `${article.title} - 黑料吃话` : "文章未找到 - 黑料吃话",
    description: article ? article.summary : "该解读文章不存在或已下线，请返回话题库浏览其他内容。",
    keywords: article ? `${article.tags.join(",")},黑料吃话,${article.category}` : "黑料吃话",
    path: `/article/${slug ?? ""}`,
    type: "article",
    image: article?.cover,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            inLanguage: "zh-CN",
            articleSection: article.category,
            keywords: article.tags.join(","),
            author: { "@type": "Person", name: article.author },
            publisher: { "@type": "Organization", name: "黑料吃话" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${window.location.origin}/` },
              { "@type": "ListItem", position: 2, name: "话题库", item: `${window.location.origin}/topics` },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${window.location.origin}/article/${article.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇解读不存在</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已被合并或调整，去话题库看看其他内容吧。</p>
          <Link
            to="/topics"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回话题库
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "话题库", to: "/topics" },
          { label: article.category, to: `/topics?category=${article.categorySlug}` },
          { label: article.title },
        ]}
      />

      <main className="flex-1">
        <article className="w-full bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="max-w-3xl">
              <span className="flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-bookmark-3-line"></i>
                {article.category}
              </span>
              <h1 className="mt-5 font-heading text-2xl font-bold leading-relaxed text-foreground-950 md:text-4xl md:leading-snug">
                {article.title}
              </h1>
              <p className="mt-5 text-sm leading-8 text-foreground-600 md:text-base">{article.summary}</p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line text-primary-600"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-primary-600"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-primary-600"></i>
                  阅读约 {article.readTime} 分钟
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-primary-600"></i>
                  {article.views.toLocaleString()} 次浏览
                </span>
              </div>
            </div>

            <div className="mt-10 h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
              <img src={article.cover} alt={article.coverAlt} className="h-full w-full object-cover object-top" />
            </div>
          </div>
        </article>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="rounded-lg border-l-4 border-primary-500 bg-primary-50 p-6">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-flag-2-line text-primary-600"></i>导读
                  </h2>
                  <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-base">{article.lead}</p>
                </div>

                <div className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-6">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-list-check-2 text-accent-700"></i>核心观点速览
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {article.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm leading-7 text-foreground-700">
                        <i className="ri-checkbox-blank-circle-fill mt-2.5 text-[6px] text-accent-600"></i>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10 space-y-10">
                  {article.sections.map((section, index) => {
                    const id = `section-${index + 1}`;
                    return (
                      <section key={section.heading} id={id} className="scroll-mt-28">
                        <h2 className="flex items-start gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                          <span className="mt-1 font-heading text-sm font-bold text-primary-500">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {section.heading}
                        </h2>
                        <div className="mt-4 space-y-4 border-l border-background-200 pl-5">
                          {section.paragraphs.map((paragraph, pi) => (
                            <p key={pi} className="text-sm leading-8 text-foreground-700 md:text-base md:leading-9">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-background-200 pt-8">
                  <span className="text-sm text-foreground-500">相关标签：</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/topics?q=${encodeURIComponent(tag)}`}
                      className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                    <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                      <i className="ri-list-unordered text-secondary-600"></i>本篇目录
                    </h2>
                    <nav aria-label="文章目录">
                      <ul className="mt-4 space-y-1">
                        {article.sections.map((section, index) => (
                          <li key={section.heading}>
                            <a
                              href={`#section-${index + 1}`}
                              className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                            >
                              <span className="text-xs text-foreground-400">{String(index + 1).padStart(2, "0")}</span>
                              {section.heading}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>

                  <div className="mt-6 rounded-lg bg-secondary-900 p-6">
                    <i className="ri-shield-check-line text-2xl text-accent-300"></i>
                    <h2 className="mt-3 font-heading text-base font-bold text-background-50">阅读提示</h2>
                    <p className="mt-2 text-sm leading-7 text-secondary-200">
                      本文为类型化案例分析，不指向现实具体个人。请把结论当作一种观察角度，保留你自己的判断。
                    </p>
                  </div>

                  <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-6">
                    <h2 className="font-heading text-base font-bold text-foreground-950">有下文线索？</h2>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">
                      如果你掌握可核验的新材料，欢迎提交，我们会持续跟进这条线索。
                    </p>
                    <Link
                      to="/contact"
                      className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-send-plane-line"></i>提交线索
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续读下去</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {relatedFill.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}